import {useNavigate} from '@github-ui/use-navigate'
import {Pagination as PrimerPagination} from '@primer/react'

import type {PaginationCursor} from '../../types/commits-types'
import {addOrModifyUrlParameters} from '../../utils/add-or-modify-url-parameters'

interface PaginationProps {
  paginationInfo: PaginationCursor
}

export function Pagination({paginationInfo}: PaginationProps) {
  const {hasPreviousPage, hasNextPage} = paginationInfo
  const navigate = useNavigate()

  // If there's only one page, don't show pagination
  if (!hasPreviousPage && !hasNextPage) {
    return null
  }

  // Map cursor-based pagination to artificial page numbers so Primer's Pagination
  // component can correctly enable/disable the Previous and Next buttons.
  // Since showPages={false}, users never see these numbers.
  let currentPage: number
  let pageCount: number

  if (!hasPreviousPage) {
    // First page — only Next is available
    currentPage = 1
    pageCount = 2
  } else if (!hasNextPage) {
    // Last page — only Previous is available
    currentPage = 2
    pageCount = 2
  } else {
    // Middle page — both Previous and Next are available
    currentPage = 2
    pageCount = 3
  }

  const hrefBuilder = (page: number): string => {
    if (page < currentPage) {
      return generateCommitPaginationUrl(paginationInfo, {isNext: false})
    } else if (page > currentPage) {
      return generateCommitPaginationUrl(paginationInfo, {isNext: true})
    }
    return '#'
  }

  const handlePageChange = (e: React.MouseEvent, page: number) => {
    e.preventDefault()
    const url = hrefBuilder(page)
    if (url !== '#') {
      navigate(url)
    }
  }

  return (
    <PrimerPagination
      pageCount={pageCount}
      currentPage={currentPage}
      hrefBuilder={hrefBuilder}
      onPageChange={handlePageChange}
      showPages={false}
    />
  )
}

function generateCommitPaginationUrl(paginationInfo: PaginationCursor, {isNext}: {isNext: boolean}) {
  let urlRecord: Record<string, string>
  if (isNext) {
    urlRecord = {after: paginationInfo.endCursor}
  } else {
    urlRecord = {before: paginationInfo.startCursor}
  }
  return addOrModifyUrlParameters(urlRecord, [isNext ? 'before' : 'after'])
}
